import React, { useState } from "react"
import { motion } from "framer-motion"
import {Download, CheckCircle, AlertCircle, Loader2, FileSpreadsheet, Info} from 'lucide-react'
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import * as XLSX from "xlsx"

interface ColecaoStatus {
  nome: string
  label: string
  total: number
  exportado: boolean
  erro: boolean
  carregando: boolean
  temBase64?: boolean
}

const COLECOES: { nome: string; label: string; temBase64?: boolean }[] = [
  { nome: "guias", label: "Guias de Sepultamento", temBase64: true },
  { nome: "pagamentos", label: "Pagamentos PIX" },
  { nome: "aforamentos", label: "Aforamentos" },
  { nome: "feirantes", label: "Feirantes" },
  { nome: "vendedores", label: "Vendedores" },
  { nome: "boletos", label: "Boletos" },
  { nome: "empresas", label: "Empresas" },
  { nome: "alvaras", label: "Alvarás" },
  { nome: "cemiterios", label: "Cemitérios" },
  { nome: "cadastro_geral", label: "Cadastro Geral" },
  { nome: "usuarios", label: "Usuários" },
  { nome: "configuracoes", label: "Configurações" },
  { nome: "taxas", label: "Taxas" },
  { nome: "alertas", label: "Alertas" },
]

const LIMITE_CELULA_EXCEL = 32000

function achatar(obj: Record<string, any>, prefixo = ""): Record<string, any> {
  const resultado: Record<string, any> = {}
  for (const chave of Object.keys(obj)) {
    const nomeChave = prefixo ? `${prefixo}.${chave}` : chave
    const valor = obj[chave]
    if (valor === null || valor === undefined) {
      resultado[nomeChave] = ""
    } else if (Array.isArray(valor)) {
      resultado[nomeChave] = JSON.stringify(valor)
    } else if (typeof valor === "object" && !(valor instanceof Date)) {
      Object.assign(resultado, achatar(valor, nomeChave))
    } else if (typeof valor === "string" && valor.length > LIMITE_CELULA_EXCEL) {
      resultado[nomeChave] = `[omitido: ${valor.length} caracteres — excede limite do Excel]`
    } else {
      resultado[nomeChave] = valor
    }
  }
  return resultado
}

function obterLista(resposta: any): any[] {
  if (Array.isArray(resposta?.list)) return resposta.list
  if (Array.isArray(resposta?.items)) return resposta.items
  if (Array.isArray(resposta?.data)) return resposta.data
  if (Array.isArray(resposta)) return resposta
  return []
}

function obterTotal(resposta: any): number | undefined {
  if (typeof resposta?.total === "number") return resposta.total
  if (typeof resposta?.pagination?.total_records === "number") {
    return resposta.pagination.total_records
  }
  if (typeof resposta?.pagination?.total === "number") return resposta.pagination.total
  return undefined
}

async function buscarPagina(colecaoNome: string, pagina: number, tamanho: number) {
  const entidade = (lumi.entities as any)?.[colecaoNome]
  if (!entidade) {
    throw new Error(`Coleção ${colecaoNome} não encontrada no SDK`)
  }

  const skip = (pagina - 1) * tamanho

  if (typeof entidade.listByPage === "function") {
    try {
      return await entidade.listByPage({
        page: pagina,
        pageSize: tamanho,
        sort: { createdAt: -1 },
      })
    } catch {
      try {
        return await entidade.listByPage(pagina, tamanho)
      } catch {
        return await entidade.list({
          limit: tamanho,
          skip,
          sort: { createdAt: -1 },
        })
      }
    }
  }

  return await entidade.list({
    limit: tamanho,
    skip,
    sort: { createdAt: -1 },
  })
}

/**
 * Exporta uma coleção página a página via listByPage.
 * Liberta certidaoBase64 e o lote da memória após escrever na planilha.
 */
async function exportarColecaoParaXLSX(
  colecaoNome: string,
  incluirAnexos: boolean,
  onProgresso: (totalAcumulado: number) => void
): Promise<{ workbook: XLSX.WorkBook; total: number }> {
  const TEM_BASE64 = colecaoNome === "guias"
  const LIMITE = TEM_BASE64 ? (incluirAnexos ? 5 : 25) : 80

  let pagina = 1
  let totalRegistros = 0
  let planilha: XLSX.WorkSheet | null = null
  let todasChaves: string[] = []

  while (true) {
    let resposta: any
    try {
      resposta = await buscarPagina(colecaoNome, pagina, LIMITE)
    } catch (err) {
      console.error(`Erro ao buscar página ${pagina} de ${colecaoNome}:`, err)
      throw err
    }

    const lista: any[] = obterLista(resposta)
    const totalServidor = obterTotal(resposta)

    if (lista.length === 0) break

    const lote = lista.map((item: any) => {
      if (!TEM_BASE64) return item
      if (!incluirAnexos) {
        const { certidaoBase64, ...resto } = item
        return resto
      }
      return item
    })

    lista.length = 0

    const achatados = lote.map((r: any) => achatar(r))
    lote.length = 0

    for (const r of achatados) {
      for (const k of Object.keys(r)) {
        if (!todasChaves.includes(k)) todasChaves.push(k)
      }
    }

    const linhas = achatados.map((r: any) => {
      const linha: Record<string, any> = {}
      for (const k of todasChaves) linha[k] = r[k] ?? ""
      return linha
    })
    achatados.length = 0

    if (!planilha) {
      planilha = XLSX.utils.json_to_sheet(linhas, { header: todasChaves })
    } else {
      XLSX.utils.sheet_add_json(planilha, linhas, {
        header: todasChaves,
        skipHeader: true,
        origin: -1,
      })
    }

    totalRegistros += linhas.length
    onProgresso(totalRegistros)
    linhas.length = 0

    if (totalServidor !== undefined && totalRegistros >= totalServidor) break
    if (obterLista(resposta).length === 0 && lista.length === 0) {
      // já esvaziámos lista; usa tamanho original via LIMITE
    }
    if (totalRegistros % LIMITE !== 0 && totalRegistros < (totalServidor ?? Infinity)) {
      // último lote incompleto
      if (totalRegistros < LIMITE * pagina) break
    }

    const veioCompleto = totalRegistros === LIMITE * pagina
    if (!veioCompleto) break

    pagina += 1
    await new Promise((r) => setTimeout(r, TEM_BASE64 ? 300 : 80))
  }

  const workbook = XLSX.utils.book_new()
  if (planilha) {
    XLSX.utils.book_append_sheet(workbook, planilha, "dados")
  } else {
    const vazia = XLSX.utils.json_to_sheet([{ info: "Nenhum registro encontrado" }])
    XLSX.utils.book_append_sheet(workbook, vazia, "dados")
  }

  return { workbook, total: totalRegistros }
}

export default function ExportarDados() {
  const [incluirAnexos, setIncluirAnexos] = useState(false)
  const [exportando, setExportando] = useState(false)
  const [status, setStatus] = useState<ColecaoStatus[]>([])
  const [concluido, setConcluido] = useState(false)

  const iniciarExportacao = async () => {
    setExportando(true)
    setConcluido(false)
    const dataHoje = new Date().toISOString().split("T")[0]

    const statusInicial: ColecaoStatus[] = COLECOES.map((c) => ({
      ...c,
      total: 0,
      exportado: false,
      erro: false,
      carregando: false,
    }))
    setStatus(statusInicial)

    let algumErro = false

    for (let i = 0; i < COLECOES.length; i++) {
      const colecao = COLECOES[i]

      setStatus((prev) =>
        prev.map((s, idx) => (idx === i ? { ...s, carregando: true, total: 0 } : s))
      )

      try {
        const { workbook, total } = await exportarColecaoParaXLSX(
          colecao.nome,
          incluirAnexos,
          (acumulado) => {
            setStatus((prev) =>
              prev.map((s, idx) => (idx === i ? { ...s, total: acumulado } : s))
            )
          }
        )

        const nomeArquivo = `EMIT_${colecao.nome}_${dataHoje}.xlsx`
        XLSX.writeFile(workbook, nomeArquivo)

        setStatus((prev) =>
          prev.map((s, idx) =>
            idx === i
              ? { ...s, total, exportado: true, carregando: false }
              : s
          )
        )
      } catch (err) {
        algumErro = true
        console.error(`Erro ao exportar ${colecao.nome}:`, err)
        setStatus((prev) =>
          prev.map((s, idx) =>
            idx === i ? { ...s, erro: true, carregando: false } : s
          )
        )
      }

      await new Promise((r) => setTimeout(r, 250))
    }

    setExportando(false)
    setConcluido(true)
    if (algumErro) {
      toast.error("Exportação concluída com erros em algumas coleções.")
    } else {
      toast.success("Exportação concluída! Os arquivos .xlsx foram baixados.")
    }
  }

  const resetar = () => {
    setStatus([])
    setConcluido(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">Como funciona a exportação</p>
          <p>
            Cada coleção é exportada individualmente como arquivo{" "}
            <strong>.xlsx</strong> (Excel), compatível com a importação do{" "}
            <strong>Lumi Cloud → Database</strong>. Os arquivos são baixados
            automaticamente no seu computador.
          </p>
          <p className="mt-1">
            As <strong>guias</strong> são lidas em lotes pequenos com{" "}
            <em>listByPage</em>. Os anexos base64 são removidos da memória após
            cada lote. Recomenda-se exportar <em>sem anexos</em> — o Excel não
            comporta ficheiros de vários MB numa célula.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <input
          type="checkbox"
          id="incluirAnexos"
          checked={incluirAnexos}
          onChange={(e) => setIncluirAnexos(e.target.checked)}
          disabled={exportando}
          className="w-4 h-4 accent-amber-600"
        />
        <label htmlFor="incluirAnexos" className="text-sm text-amber-900 cursor-pointer">
          <span className="font-semibold">
            Incluir certidões de óbito (base64) nas guias
          </span>
          <span className="block text-xs text-amber-700 mt-0.5">
            ⚠️ Células Excel têm limite de ~32 mil caracteres. Anexos grandes
            serão omitidos automaticamente para não corromper o ficheiro.
          </span>
        </label>
      </div>

      {!exportando && !concluido ? (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={iniciarExportacao}
          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#004161] text-white rounded-xl hover:bg-[#005580] transition-colors shadow-lg text-base font-semibold"
        >
          <Download className="w-5 h-5" />
          Iniciar Exportação Completa ({COLECOES.length} coleções)
        </motion.button>
      ) : null}

      {exportando ? (
        <div className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-100 rounded-xl text-gray-600 font-medium">
          <Loader2 className="w-5 h-5 animate-spin" />
          Exportando dados em lotes, aguarde…
        </div>
      ) : null}

      {concluido ? (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={resetar}
          className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
        >
          Nova Exportação
        </motion.button>
      ) : null}

      {status.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Progresso da exportação:
          </h4>
          {status.map((s) => (
            <div
              key={s.nome}
              className={`flex items-center gap-3 p-3 rounded-lg border text-sm transition-all ${
                s.exportado
                  ? "bg-green-50 border-green-200"
                  : s.erro
                  ? "bg-red-50 border-red-200"
                  : s.carregando
                  ? "bg-blue-50 border-blue-200"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              {s.carregando ? (
                <Loader2 className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
              ) : null}
              {s.exportado ? (
                <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
              ) : null}
              {s.erro ? (
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              ) : null}
              {!s.carregando && !s.exportado && !s.erro ? (
                <FileSpreadsheet className="w-4 h-4 text-gray-400 shrink-0" />
              ) : null}

              <span className="flex-1 font-medium text-gray-800">{s.label}</span>

              {s.exportado ? (
                <span className="text-xs text-green-700 font-medium">
                  {s.total} registro{s.total !== 1 ? "s" : ""} ✓
                </span>
              ) : null}
              {s.erro ? (
                <span className="text-xs text-red-600 font-medium">Erro</span>
              ) : null}
              {s.carregando ? (
                <span className="text-xs text-blue-600 font-medium">
                  {s.total > 0 ? `${s.total} lidos…` : "Exportando…"}
                </span>
              ) : null}
              {!s.carregando && !s.exportado && !s.erro ? (
                <span className="text-xs text-gray-400">Aguardando</span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {concluido ? (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">
              Exportação concluída!
            </span>
          </div>
          <p className="text-sm text-green-700">
            Os arquivos <strong>.xlsx</strong> foram baixados para o seu
            computador.
          </p>
          <p className="text-sm text-green-700 mt-1">
            Para importar no novo projeto: acesse{" "}
            <strong>Cloud → Database</strong>, selecione a coleção desejada e
            clique em <strong>Import</strong>, escolhendo o arquivo{" "}
            <strong>.xlsx</strong> correspondente.
          </p>
        </div>
      ) : null}
    </div>
  )
}
